/**
 * ErrorBoundary - Portal wrapper around lib's ErrorBoundary.
 *
 * Wires i18n labels and forwards caught errors to backend logging via
 * `logErrorToBackend`. Consumers may pass extra props (`fallback`, `onReset`,
 * `showErrorDetails`) — they pass through to the lib component.
 */

import React, { type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ErrorBoundary as LibErrorBoundary,
  type ErrorBoundaryProps as LibErrorBoundaryProps,
} from '@/components/ui';
import { NS } from '@/i18n/namespaces';
import { logErrorToBackend } from '@/infrastructure/services/error-log-service';

export type ErrorBoundaryProps = Omit<
  LibErrorBoundaryProps,
  'fallbackTitle' | 'fallbackMessage' | 'retryLabel'
> & {
  fallbackTitle?: string;
  fallbackMessage?: string;
  retryLabel?: string;
};

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  fallbackTitle,
  fallbackMessage,
  retryLabel,
  onError,
  children,
  ...rest
}) => {
  const { t } = useTranslation(NS.COMMON);

  const handleError = (error: Error, info: ErrorInfo) => {
    logErrorToBackend(error, info);
    onError?.(error, info);
  };

  return (
    <LibErrorBoundary
      fallbackTitle={fallbackTitle ?? t('ErrorBoundary_Title', 'Oops! Something went wrong')}
      fallbackMessage={
        fallbackMessage ??
        t(
          'ErrorBoundary_Description',
          "We're sorry for the inconvenience. The error has been logged and our team will investigate."
        )
      }
      retryLabel={retryLabel ?? t('ErrorBoundary_TryAgain', 'Try Again')}
      onError={handleError}
      showErrorDetails={process.env.NODE_ENV === 'development'}
      {...rest}
    >
      {children as ReactNode}
    </LibErrorBoundary>
  );
};

ErrorBoundary.displayName = 'ErrorBoundary';

export default ErrorBoundary;
